import { useQuery } from "@tanstack/react-query";
import { supabase } from "./supabase";
import { useOrgStore } from "@/stores/orgStore";
import { useAuth } from "./auth";
import { useEffect } from "react";

export interface Organization {
  id: string;
  name: string;
  slug: string;
  country_code: string;
  default_currency: string;
  default_locale: string;
  default_timezone: string;
  subscription_tier: string;
  trial_ends_at: string | null;
  created_at: string;
  updated_at: string;
}

export interface OrganizationMembership {
  organization_id: string;
  user_id: string;
  role: "owner" | "admin" | "manager" | "staff" | "viewer";
  organization: Organization;
}

/**
 * Fetch all organizations the current user belongs to.
 * Returns memberships with embedded organization details.
 */
export function useUserOrganizations() {
  const { user } = useAuth();

  return useQuery({
    queryKey: ["user-organizations", user?.id],
    enabled: !!user,
    queryFn: async (): Promise<OrganizationMembership[]> => {
      const { data, error } = await supabase
        .from("organization_members")
        .select(
          "organization_id, user_id, role, organization:organizations(*)",
        )
        .eq("user_id", user!.id);

      if (error) throw error;
      return (data ?? []) as unknown as OrganizationMembership[];
    },
  });
}

/**
 * Returns the currently selected organization, syncing from store.
 * If no org selected but memberships exist, defaults to the first.
 */
export function useCurrentOrganization() {
  const { data: memberships, isLoading } = useUserOrganizations();
  const currentOrgId = useOrgStore((s) => s.currentOrgId);
  const setCurrentOrgId = useOrgStore((s) => s.setCurrentOrgId);

  useEffect(() => {
    if (memberships && memberships.length > 0 && !currentOrgId) {
      setCurrentOrgId(memberships[0].organization_id);
    }
  }, [memberships, currentOrgId, setCurrentOrgId]);

  const current =
    memberships?.find((m) => m.organization_id === currentOrgId) ?? null;

  return {
    organization: current?.organization ?? null,
    membership: current ?? null,
    memberships: memberships ?? [],
    loading: isLoading,
  };
}

export async function createOrganization(input: {
  name: string;
  countryCode: string;
  defaultCurrency: string;
  defaultLocale: string;
  defaultTimezone: string;
}): Promise<Organization> {
  const slug = slugify(input.name);

  const { data, error } = await supabase.rpc("create_organization", {
    p_name: input.name,
    p_slug: slug,
    p_country_code: input.countryCode,
    p_default_currency: input.defaultCurrency,
    p_default_locale: input.defaultLocale,
    p_default_timezone: input.defaultTimezone,
  });

  if (error) throw error;
  return data as Organization;
}

function slugify(value: string): string {
  return value
    .toLowerCase()
    .normalize("NFKD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/(^-|-$)/g, "")
    .slice(0, 60);
}
